import { nowUtcIso } from '@/lib/dates'
import type { WorkspaceDb } from './db'
import type { ApplyOperationResult, SyncTransport } from './transport'
import type { OutboxOperation } from './types'

export interface PushOptions {
  /** Max operations to attempt in one drain. Default 50. */
  batchLimit?: number
}

export interface PushSummary {
  processed: number
  applied: number
  replayed: number
  /** Ops the server rejected as a same-revision conflict; local drafts preserved for resolution. */
  conflicts: OutboxOperation[]
  /** Definitive business rejections (invalid_input / duplicate_imei / already_sold). */
  rejected: Array<{ operation: OutboxOperation; result: ApplyOperationResult }>
  /** A transport error occurred; outcome unknown, queue preserved, drain stopped. */
  stoppedOffline: boolean
  /** Server said unauthorized; likely a session/membership issue, drain stopped. */
  stoppedUnauthorized: boolean
}

/**
 * Drain the outbox in FIFO order through the transport.
 *
 * Guarantees (plan section 8, 16):
 * - Idempotent: the same operation_id retried after an unknown-outcome failure comes
 *   back as 'replayed' and is treated as success (removed once).
 * - Offline-safe: a transport throw does NOT delete the op; it is retried later.
 * - No silent last-write-wins: 'conflict' keeps the local draft in the queue and is
 *   surfaced for explicit owner resolution.
 * - Definitive rejections leave the queue (stop retrying) but never delete the local record.
 */
export async function pushOutbox(
  db: WorkspaceDb,
  transport: SyncTransport,
  opts: PushOptions = {},
): Promise<PushSummary> {
  const batchLimit = opts.batchLimit ?? 50

  const ops = await db.outbox.orderBy('created_at').limit(batchLimit).toArray()
  ops.sort((a, b) =>
    a.created_at === b.created_at ? a.operation_id.localeCompare(b.operation_id) : 0,
  )

  const summary: PushSummary = {
    processed: 0,
    applied: 0,
    replayed: 0,
    conflicts: [],
    rejected: [],
    stoppedOffline: false,
    stoppedUnauthorized: false,
  }

  for (const op of ops) {
    summary.processed++

    let result: ApplyOperationResult
    try {
      result = await transport({
        operation_id: op.operation_id,
        workspace_id: op.workspace_id,
        entity_id: op.entity_id,
        expected_revision: op.expected_revision,
        action: op.action,
        payload: op.payload,
      })
    } catch (err) {
      // Unknown outcome. Preserve the op; retry later with the SAME operation_id.
      await db.outbox.update(op.operation_id, {
        attempts: op.attempts + 1,
        last_error: err instanceof Error ? err.message : String(err),
      })
      summary.stoppedOffline = true
      break
    }

    switch (result.status) {
      case 'applied':
      case 'replayed': {
        await db.transaction('rw', db.outbox, db.pull_cursor, async () => {
          await db.outbox.delete(op.operation_id)
          if (typeof result.server_sequence === 'number') {
            await advanceCursor(db, op.workspace_id, result.server_sequence)
          }
        })
        if (result.status === 'applied') summary.applied++
        else summary.replayed++
        break
      }
      case 'conflict': {
        await db.outbox.update(op.operation_id, { last_error: 'conflict' })
        summary.conflicts.push({ ...op, last_error: 'conflict' })
        break
      }
      case 'unauthorized': {
        summary.stoppedUnauthorized = true
        return summary
      }
      default: {
        // invalid_input | duplicate_imei | already_sold: definitive, non-retryable.
        await db.outbox.delete(op.operation_id)
        summary.rejected.push({ operation: op, result })
        break
      }
    }
  }

  return summary
}

async function advanceCursor(db: WorkspaceDb, workspaceId: string, seq: number): Promise<void> {
  const existing = await db.pull_cursor.get(workspaceId)
  const next = Math.max(existing?.last_server_sequence ?? 0, seq)
  await db.pull_cursor.put({
    workspace_id: workspaceId,
    last_server_sequence: next,
    updated_at: nowUtcIso(),
  })
}
